import { paths } from '@/constants/paths'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import { getGuildSale, GuildSale } from '@/services/SaleService'
import { shouldShowAvailabilityWarning } from '@/utils/GuildUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildIntervalView } from '@/views/GuildSignupView/GuildIntervalView'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildIntervalPageProps {
  locale: string
  preview: boolean
  slug?: string
  tier: 'basic' | 'premium'
  promotion: Promotion | null
  sale: GuildSale | null
  region: string | null
  shouldShowAvailabilityWarning: boolean
}

const GuildIntervalPage: NextPageWithLayout<GuildIntervalPageProps> = ({
  locale,
  tier,
  promotion,
  sale,
  region,
  shouldShowAvailabilityWarning,
}) => {
  const { t } = useTranslate('guild')
  const title = t('angelGuildPlanTitle', 'Angel Guild Membership Plan Selection | Angel')
  const description = t(
    'angelStudiosGuildJoinInfoMetaDescriptionV3',
    'Join the Angel Guild to vote on upcoming shows, get free tickets to theatrical releases, early access, and merchandise discounts.',
  )

  return (
    <>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp',
          cloudinaryTransformation: 'b_rgb:000000',
          description,
          locale,
          path: `${paths.guild.interval.index}/${tier}`,
          title,
        })}
      />
      <GuildSignupContextProvider
        promotion={promotion}
        sale={sale}
        region={region}
        shouldShowAvailabilityWarning={shouldShowAvailabilityWarning}
      >
        <GuildIntervalView tier={tier} />
      </GuildSignupContextProvider>
    </>
  )
}

GuildIntervalPage.getLayout = (page) => {
  return <ContentOnlyLayout>{page}</ContentOnlyLayout>
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ locale, preview, params, federationClient, region }) => {
    const tier = getItemOrFirstEntry(params?.tier)

    if (!tier) {
      return { redirect: { destination: paths.guild.pricing, permanent: false } }
    }

    const [promotion, sale, translations] = await Promise.all([
      getGuildPromotion({ opts: { locale, preview }, federationClient }),
      getGuildSale({ opts: { preview, locale }, federationClient }),
      loadTranslations(locale, ['common', 'guild', 'join-the-guild', 'guild-benefits']),
    ])

    return {
      props: {
        locale,
        preview,
        tier,
        promotion,
        sale,
        region,
        shouldShowAvailabilityWarning: shouldShowAvailabilityWarning(region),
        ...translations,
      },
    }
  },
)

export default GuildIntervalPage
